import React, { useMemo, useState } from 'react';
import { ScrollView, StyleSheet, Text, View } from 'react-native';

import { colors, spacing, typography } from '../../core/theme/tokens';
import type { AnalyticsDailyPoint } from '../../types/analytics';
import {
  WEEKDAY_LABELS,
  buildSpendCalendarLayout,
  cellRect,
  spendHeatColor,
} from './d3/calendarLayout';
import { formatCurrency, useChartWidth } from './d3';
import { SvgChartCanvas } from './web/SvgChartCanvas.web';

type Props = {
  year: number;
  days: AnalyticsDailyPoint[];
  /** Optional month key `YYYY-MM` to highlight (dim other months). */
  highlightMonthKey?: string;
  onDayPress?: (date: string, amount: number) => void;
};

/**
 * Observable-style year calendar heatmap — brighter cells = more spend.
 * Web: DOM SVG.
 */
export function SpendCalendarHeatmap({ year, days, highlightMonthKey, onDayPress }: Props) {
  const { width, onLayout } = useChartWidth(360);
  const [hovered, setHovered] = useState<string | null>(null);

  const layout = useMemo(
    () =>
      buildSpendCalendarLayout({
        year,
        days,
        // Prefer filling the card width; scroll only when the year grid is wider.
        width: Math.max(width, 280),
      }),
    [year, days, width],
  );

  const highlightMonth = highlightMonthKey?.startsWith(`${year}-`)
    ? Number(highlightMonthKey.slice(5, 7)) - 1
    : null;

  const hoveredCell = hovered ? layout.cells.find((c) => c.date === hovered) : null;

  return (
    <View style={styles.wrap} onLayout={onLayout}>
      <ScrollView horizontal showsHorizontalScrollIndicator={false}>
        <SvgChartCanvas width={layout.width} height={layout.height}>
          {layout.monthLabels.map((m) => (
            <text
              key={m.label + m.x}
              x={m.x}
              y={12}
              fill={colors.textMuted}
              fontSize={10}
              fontFamily="IBM Plex Sans, system-ui, sans-serif"
            >
              {m.label}
            </text>
          ))}

          {WEEKDAY_LABELS.map((label, i) => (
            <text
              key={label}
              x={layout.leftPad - 6}
              y={layout.topPad + i * (layout.cellSize + layout.gap) + layout.cellSize * 0.78}
              fill={colors.textMuted}
              fontSize={10}
              fontFamily="IBM Plex Sans, system-ui, sans-serif"
              textAnchor="end"
            >
              {label}
            </text>
          ))}

          {layout.cells.map((cell) => {
            const { x, y, size } = cellRect(layout, cell);
            const dimmed = highlightMonth != null && cell.month !== highlightMonth;
            const isHovered = hovered === cell.date;
            return (
              <rect
                key={`${cell.date}:${cell.amount}`}
                x={x}
                y={y}
                width={size}
                height={size}
                rx={2}
                ry={2}
                fill={layout.colorFor(cell.amount)}
                opacity={dimmed && !isHovered ? 0.28 : 1}
                stroke={isHovered ? colors.text : 'transparent'}
                strokeWidth={isHovered ? 1 : 0}
                onMouseEnter={() => setHovered(cell.date)}
                onMouseLeave={() => setHovered((h) => (h === cell.date ? null : h))}
                onClick={() => onDayPress?.(cell.date, cell.amount)}
              >
                <title>
                  {cell.date}: {formatCurrency(cell.amount)}
                </title>
              </rect>
            );
          })}
        </SvgChartCanvas>
      </ScrollView>

      <View style={styles.footer}>
        <Text style={styles.hint} numberOfLines={2}>
          {hoveredCell
            ? `${hoveredCell.date} · ${WEEKDAY_LABELS[hoveredCell.dayOfWeek]} · ${formatCurrency(hoveredCell.amount)}`
            : 'Rows Mon→Sun · brighter = more spent'}
        </Text>
        <View style={styles.legend}>
          <Text style={styles.legendLabel}>Less</Text>
          {[0, 0.25, 0.5, 0.75, 1].map((t) => (
            <View
              key={t}
              style={[styles.legendSwatch, { backgroundColor: spendHeatColor(t) }]}
            />
          ))}
          <Text style={styles.legendLabel}>More</Text>
        </View>
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    gap: spacing.md,
    overflow: 'visible',
  },
  footer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    paddingTop: spacing.xs,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: colors.glassBorder,
  },
  hint: {
    ...typography.caption,
    color: colors.textMuted,
    fontVariant: ['tabular-nums'],
    flex: 1,
    minWidth: 0,
  },
  legend: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 0,
  },
  legendLabel: {
    ...typography.caption,
    color: colors.textMuted,
    fontSize: 11,
  },
  legendSwatch: {
    width: 12,
    height: 12,
    borderRadius: 3,
  },
});
